import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useInitializer from "../../../../../hooks/useInitializer";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminCurveIntermediate } from "../../../../../http/patrol/types/curveIntermediates";
import { useCurvesIntermediatesApi } from "../../../../../http/patrol/useCurveIntermediates";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateCurveIntermediateModalDialogButton from "./CreateCurveIntermediateModalDialogButton";
import CurveIntermediateForm from "./CurveIntermediateForm";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const CurveIntermediates: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const { patrolSystemId } = useParams<{ patrolSystemId: string }>();
  const filters = useListFilters({ key: "curveIntermediates" });
  const aborter = useRef(new AbortController());
  const curvesIntermediatesApi = useCurvesIntermediatesApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "curveIntermediates");
  const isActiveTab = active ?? true;
  const debouncedValue = useDebounce(searchTerm);
  const [itemToDelete, setItemToDelete] = useState<AdminCurveIntermediate>();
  const { accessesAction, accessesDialog } = useEntityAccesses<AdminCurveIntermediate>("patrol.curve_intermediate");
  const [itemToEdit, setItemToEdit] = useState<AdminCurveIntermediate>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { data, error } = curvesIntermediatesApi.useAdminCurvesIntermediates(
    query,
    aborter.current.signal,
    patrolSystemId,
  );

  const isInitialized = useInitializer(data || error);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.patrolCurveIntermediates.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.patrolCurveIntermediates.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateCurveIntermediateModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  if (!isInitialized) {
    return;
  }

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await curvesIntermediatesApi.updateAdminCurveIntermediateVisibility(id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <>
      <FlexContent padding>
        <Table<AdminCurveIntermediate>
          filters={filters}
          isTall
          head={
            <tr>
              <th align="left">{i18n.translation.common.codeItem}</th>
              <th align="left">{i18n.translation.common.codeExtraItem}</th>
              <th align="left">{i18n.translation.common.description}</th>
              {!patrolSystemId && <th align="left">{i18n.translation.systemTypes.plural}</th>}
              <th align="left">{`${i18n.translation.common.angle} (${i18n.translation.common.min}/${i18n.translation.common.max})`}</th>
              <th>{i18n.translation.common.visibility}</th>
              <th></th>
            </tr>
          }
          items={data?.items}
          renderRow={(item) => (
            <>
              <td>
                <Link onClick={(e) => e.stopPropagation()} to={`/admin/items/${item.item.id}`}>
                  {item.item.code}
                </Link>
              </td>
              <td>
                {item.extraItem ? (
                  <Link onClick={(e) => e.stopPropagation()} to={`/admin/items/${item.extraItem.id}`}>
                    {item.extraItem?.code}
                  </Link>
                ) : undefined}
              </td>
              <td>{item.item.description}</td>
              {!patrolSystemId && <td>{item.systems.map((x) => x.name).join("; ")}</td>}
              <td>
                {item.minAngle}° / {item.maxAngle}°
              </td>

              <td align="center">
                <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
              </td>
            </>
          )}
          onRowClick={(item) => setItemToEdit(item)}
          rowActions={(item) => [
            getEditAction(i18n, () => setItemToEdit(item)),
            accessesAction(item),
            getDeleteAction(i18n, () => setItemToDelete(item), true),
          ]}
          error={!!error}
        />
        {accessesDialog}
        {!!itemToEdit && (
          <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.patrolCurveIntermediates.edit}>
            <CurveIntermediateForm onClose={() => setItemToEdit(undefined)} curveIntermediate={itemToEdit} />
          </ModalDialog>
        )}
        {!!itemToDelete && (
          <ModalDialog
            onClose={() => setItemToDelete(undefined)}
            title={i18n.translation.patrolCurveIntermediates.delete}
            isSubmitting={isSubmitting}
            actions={[
              {
                title: i18n.translation.common.cancel,
                onClick: () => setItemToDelete(undefined),
                type: "secondary",
              },
              {
                title: i18n.translation.common.yes,
                onClick: () => {
                  setIsSubmitting(true);
                  curvesIntermediatesApi
                    .deleteAdminCurveIntermediate(itemToDelete.id)
                    .then(() => setItemToDelete(undefined))
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                },
              },
            ]}
          >
            {i18n.translation.patrolCurveIntermediates.questions.delete}
          </ModalDialog>
        )}
      </FlexContent>
    </>
  );
};

export default CurveIntermediates;
